import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, FileText, History } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { closeSearchModal, closeViewer } from '../../redux/slices/userDocumentSearchSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { timelineSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';
import KnowledgebaseContent from './knowledgebase-content';
import KnowledgebaseHeader from './knowledgebase-header';
import SearchDocumentModal from './search-document-modal';
import SearchHistoryView from './search-history-view';
import SmartSearchViewer from './smart-search-viewer';
import TimelineView from './timeline/timeline-view';

export default function Knowledgebase() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentCaseId = useAppSelector(state => state.CaseReducer.currentCaseId);
  const [activeTab, setActiveTab] = useState<'documents' | 'timeline' | 'history'>('documents');

  // Get knowledgebase for current case
  const knowledgebase = useAppSelector(state =>
    state.DBKnowledgebaseReducer.ids
      .map(id => state.DBKnowledgebaseReducer.entities[id])
      .find(kb => kb?.caseId === currentCaseId)
  );

  // Get timeline for this knowledgebase to check for updates
  const timeline = useAppSelector(state =>
    knowledgebase?._id ? timelineSelectOneObjectByQuery(state.DBTimelineReducer, { knowledgebaseId: knowledgebase._id }) : null
  );

  const {
    isModalOpen,
    isViewerOpen,
    searchResults,
    queryText
  } = useAppSelector(state => state.userDocumentSearch);

  const documents = useAppSelector(state => {
    if (!knowledgebase?._id) return [];
    return Object.values(state.DBDocumentReducer.entities).filter((doc: any) => doc.knowledgebaseId === knowledgebase._id) as any[];
  });

  return (
    <Card
      className="h-full w-full rounded-none overflow-hidden flex flex-col shadow-md border"
    >
      {/* Premium Header Component */}
      <KnowledgebaseHeader
        activeTab={activeTab}
      />

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'documents' | 'timeline' | 'history')} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="w-full rounded-none border-b h-12 flex-shrink-0">
          <TabsTrigger value="documents" className="flex items-center gap-2 flex-1">
            <FileText className="h-4 w-4" />
            {t('common.navigation.documents')}
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-2 flex-1 relative">
            <Clock className="h-4 w-4" />
            {t('common.navigation.timeline')}
            {timeline?.needsUpdate && (
              <Badge variant="destructive" className="ml-1 h-2 w-2 p-0 rounded-full" />
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2 flex-1">
            <History className="h-5 w-5" />
            {t('knowledgebase.searchHistory', 'Search History')}
          </TabsTrigger>
        </TabsList>

        {/* Main Content */}
        <TabsContent value="documents" className="flex-1 m-0 overflow-hidden">
          <KnowledgebaseContent />
        </TabsContent>
        <TabsContent value="timeline" className="flex-1 m-0 overflow-hidden">
          <TimelineView />
        </TabsContent>
        <TabsContent value="history" className="flex-1 m-0 overflow-hidden">
          <SearchHistoryView />
        </TabsContent>
      </Tabs>

      <SearchDocumentModal
        open={isModalOpen}
        onClose={() => dispatch(closeSearchModal())}
        allDocuments={documents}
      />

      <SmartSearchViewer
        open={isViewerOpen}
        onClose={() => dispatch(closeViewer())}
        searchResults={searchResults}
        query={queryText}
      />
    </Card>
  )
}
